import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { showToast } from '../utils/toasts';
import { useTheme as useCustomTheme } from '../context/ThemeContext';

const AuthCallback: React.FC = () => {
  const { mode } = useCustomTheme();

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';

  const background =
    mode === 'dark'
      ? lokiGradient
      : '#ffffff';

  const primaryTextColor =
    mode === 'dark' ? '#ffffff' : '#051814';

  const secondaryTextColor =
    mode === 'dark'
      ? 'rgba(255,255,255,0.7)'
      : 'rgba(5,24,20,0.6)';

  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthData } = useAuth();

  useEffect(() => {
    let isMounted = true;
    let errorToastShown = false;

    const processAuth = async () => {
      console.log('🟡 AuthCallback processing...');
      
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const error = params.get('error');

      console.log('📊 URL params:', { token, error });

      if (error) {
        console.error('❌ Error from backend:', error);
        errorToastShown = true;
        showToast.googleLoginError(decodeURIComponent(error));
        navigate('/login', { replace: true });
        return;
      }

      if (!token) {
        console.error('❌ No token found in URL');
        errorToastShown = true;
        showToast.googleLoginError('No authentication token received');
        navigate('/login', { replace: true });
        return;
      }

      try {
        console.log('🔑 Setting token:', token.substring(0, 20) + '...');
        
        // Clear any existing headers first to avoid conflicts
        delete axios.defaults.headers.common['Authorization'];
        
        // Set axios header with the new token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        console.log('👤 Fetching user data...');
        
        const response = await axios.get('/api/auth/me');
        
        console.log('📦 User API response:', response.data);
        
        if (response.data.success && response.data.user) {
          const user = response.data.user;
          console.log('✅ User data received:', user.email);
          
          // Use setAuthData to update context and localStorage
          setAuthData(token, user);
          
          // Show success toast
          showToast.googleLoginSuccess();
          
          console.log('🚀 Redirecting to profile...');
          // Navigate immediately without delay
          navigate('/profile', { replace: true });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err: any) {
        console.error('❌ Failed to fetch user:', err);
        console.error('Error details:', err.response?.data);
        
        // Only handle error if component is still mounted and we haven't shown error toast yet
        if (isMounted && !errorToastShown) {
          // Clear invalid token
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          
          showToast.googleLoginError('Failed to load user profile');
          navigate('/login', { replace: true });
        }
      }
    };

    processAuth();

    // Cleanup function - only prevents toast display after unmount
    return () => {
      isMounted = false;
    };
  }, [location, navigate, setAuthData]);

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background,
        transition: 'background 0.3s ease'
      }}
    >
      <Box textAlign="center">
        <CircularProgress
          size={90}
          thickness={4}
          sx={{ color: primaryTextColor }}
        />

        <Typography
          variant="h4"
          sx={{
            mt: 4,
            fontWeight: 700,
            color: primaryTextColor
          }}
        >
          Completing sign in…
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mt: 1.5,
            color: secondaryTextColor
          }}
        >
          Please wait a moment
        </Typography>
      </Box>
    </Container>
  );
};

export default AuthCallback;